/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/lots_notes_junction-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-lots_notes_junction-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/lots_notes_junction-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, lot_id, note_id, trig_organization_id
 * - Sortable fields: created_at, updated_at
 * - Required fields: lot_id, note_id
 * - Readonly fields: id, created_at, updated_at, trig_lot_address, trig_lot_number, trig_lot_status_name, trig_organization_id, trig_note_content, trig_note_title, trig_note_created_by, trig_note_is_internal, trig_note_type
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/lots_notes_junction-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-lots_notes_junction-api-client.ts (API client)
 * - client/api/generated_schemas/lots_notes_junction.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-08-20T18:11:43.865Z
 * Source config: config/entities/lots_notes_junction-entity-config.ts
 * Public fields: 15/15 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LotsNotesJunctionClient, LotsNotesJunctionQueryOptions } from '../generated_clients/generated-lots_notes_junction-api-client.js';
import {
  LotsNotesJunction,
  LotsNotesJunctionInsert,
  LotsNotesJunctionUpdate,
  LotsNotesJunctionFieldConfig
} from '../generated_schemas/generated-lots_notes_junction-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/lots_notes_junction_business_hooks/

// Query keys with enhanced caching strategy
export const lotsNotesJunctionKeys = {
  all: ['lots_notes_junction'] as const,
  lists: () => [...lotsNotesJunctionKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...lotsNotesJunctionKeys.lists(), { filters }] as const,
  details: () => [...lotsNotesJunctionKeys.all, 'detail'] as const,
  detail: (id: string) => [...lotsNotesJunctionKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...lotsNotesJunctionKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...lotsNotesJunctionKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...lotsNotesJunctionKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...lotsNotesJunctionKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useLotsNotesJunctionClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new LotsNotesJunctionClient();
};

/**
 * Hook to get all lots_notes_junction with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with lots_notes_junction data
 */
export const useLotsNotesJunctionList = (options?: LotsNotesJunctionQueryOptions) => {
  const client = useLotsNotesJunctionClient();

  return useQuery({
    queryKey: lotsNotesJunctionKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
    // Always reconcile with the server on mount, even when persisted/cached
    // data is restored (field PWA offline cache) or a cross-device write
    // happened elsewhere. Cached data still paints instantly; the refetch
    // corrects it. Offline, the refetch simply fails and the cache is kept.
    // Nobody should ever see stale operational data. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * Hook to get a single lots_notes_junctio by ID
 *
 * @param id - The lots_notes_junctio ID
 * @returns React Query result with single lots_notes_junctio data
 */
export const useLotsNotesJunction = (id: string) => {
  const client = useLotsNotesJunctionClient();

  return useQuery({
    queryKey: lotsNotesJunctionKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
    // Always reconcile with the server on mount so a reload / navigation never
    // paints a stale restored (persisted) detail snapshot. (card rKkWHT_XhbXU)
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/lots_notes_junction
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/lots_notes_junction_business_hooks/
 */

/**
 * Hook to create a new lots_notes_junctio
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: lot_id, note_id
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at, trig_lot_address, trig_lot_number, trig_lot_status_name, trig_organization_id, trig_note_content, trig_note_title, trig_note_created_by, trig_note_is_internal, trig_note_type
 *
 * @returns React Query mutation for creating lots_notes_junctio
 */
export const useCreateLotsNotesJunction = () => {
  const client = useLotsNotesJunctionClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LotsNotesJunctionInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lotsNotesJunctionKeys.all });
    },
  });
};

/**
 * Hook to update an existing lots_notes_junctio
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at, trig_lot_address, trig_lot_number, trig_lot_status_name, trig_organization_id, trig_note_content, trig_note_title, trig_note_created_by, trig_note_is_internal, trig_note_type
 *
 * @returns React Query mutation for updating lots_notes_junctio
 */
export const useUpdateLotsNotesJunction = () => {
  const client = useLotsNotesJunctionClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LotsNotesJunctionUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: lotsNotesJunctionKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: lotsNotesJunctionKeys.lists() });
    },
  });
};

/**
 * Hook to delete a lots_notes_junctio
 *
 * @returns React Query mutation for deleting lots_notes_junctio
 */
export const useDeleteLotsNotesJunction = () => {
  const client = useLotsNotesJunctionClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lotsNotesJunctionKeys.all });
    },
  });
};

/**
 * Hook to count lots_notes_junction with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, lot_id, note_id, trig_organization_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useLotsNotesJunctionCount = (filter?: Partial<Record<typeof LotsNotesJunctionFieldConfig.filterable[number], any>>) => {
  const client = useLotsNotesJunctionClient();
  
  return useQuery({
    queryKey: lotsNotesJunctionKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useLotsNotesJunctionFieldConfig = () => {
  return LotsNotesJunctionFieldConfig;
};